import { useMemo } from 'react';
import type { FHRequestMessage, TimelineItem, DelayTimelineEvent } from '../types';
import type { Email } from '../api/email_api';

/**
 * Get the date from any timeline item for sorting.
 */
function getItemDate(item: TimelineItem): string {
  if (item.kind === 'delay_event') {
    return item.data.date;
  }
  return item.data.date_created;
}

/**
 * Merges messages, outgoing emails, and delay events into a single
 * chronologically sorted timeline. Filters out "Email sent to" system messages
 * when outgoing emails are present (avoids duplication since the backend
 * creates a system message when an email is sent).
 */
export function useTimelineItems(
  messages: FHRequestMessage[],
  emails: Email[],
  perspective: 'client' | 'assistant',
  delayEvents: DelayTimelineEvent[] = []
): TimelineItem[] {
  return useMemo(() => {
    const hasEmails = emails.length > 0;

    // Wrap messages, filtering out duplicate "Email sent to" system messages
    const messageItems: TimelineItem[] = messages
      .filter((msg) => {
        if (
          hasEmails &&
          msg.message_type === 'system' &&
          /^Email sent to\b/i.test(msg.content)
        ) {
          return false;
        }
        return true;
      })
      .map((msg) => ({ kind: 'message' as const, data: msg }));

    // Only include outgoing emails in assistant view
    const emailItems: TimelineItem[] =
      perspective === 'assistant'
        ? emails.map((email) => ({ kind: 'outgoing_email' as const, data: email }))
        : [];

    // Only include delay events in assistant view
    const delayItems: TimelineItem[] =
      perspective === 'assistant'
        ? delayEvents.map((event) => ({ kind: 'delay_event' as const, data: event }))
        : [];

    // Merge and sort chronologically (oldest first, chat order)
    const all = [...messageItems, ...emailItems, ...delayItems];
    all.sort((a, b) => {
      const dateA = new Date(getItemDate(a)).getTime();
      const dateB = new Date(getItemDate(b)).getTime();
      return dateA - dateB;
    });

    return all;
  }, [messages, emails, perspective, delayEvents]);
}
